/**
 * Liens légaux affichés sur les écrans publics (connexion, inscription…) à partir des champs de l'organisme
 * (Admin → Organisme → « Adresse de l'application et pages légales »). Audit du 04/11/2026 (P1-5).
 *
 * La politique de confidentialité n'a pas de champ propre : elle est déduite du site de l'organisme
 * (`<site_web>/politique-de-confidentialite`, page présente sur skills4mation.com et formatrix.fr).
 */
export type ChampsLegaux = { site_web?: string | null; mentions_legales?: string | null; cgu?: string | null };
export type LienLegal = { href: string; libelle: string };

const httpOuRien = (brut: string | null | undefined): URL | null => {
  if (!brut?.trim()) return null;
  try {
    const url = new URL(brut.trim());
    return url.protocol === "https:" || url.protocol === "http:" ? url : null;
  } catch {
    return null;
  }
};

/** Politique de confidentialité de l'organisme, ou null sans site renseigné. */
export function lienConfidentialite(champs: ChampsLegaux | null | undefined): string | null {
  const site = httpOuRien(champs?.site_web);
  return site ? new URL("/politique-de-confidentialite", site.origin).href : null;
}

/** Liens à afficher, sans doublon : une même adresse pour les mentions et les CGU donne un seul lien. */
export function liensLegaux(champs: ChampsLegaux | null | undefined): LienLegal[] {
  const mentions = httpOuRien(champs?.mentions_legales)?.href ?? null;
  const cgu = httpOuRien(champs?.cgu)?.href ?? null;
  const site = httpOuRien(champs?.site_web)?.href ?? null;
  const confidentialite = lienConfidentialite(champs);
  const liens: LienLegal[] = [];
  if (mentions && cgu === mentions) liens.push({ href: mentions, libelle: "Mentions légales et CGU" });
  else {
    if (mentions) liens.push({ href: mentions, libelle: "Mentions légales" });
    if (cgu) liens.push({ href: cgu, libelle: "Conditions générales d'utilisation" });
  }
  if (confidentialite) liens.push({ href: confidentialite, libelle: "Confidentialité" });
  if (site) liens.push({ href: site, libelle: "Site de l'organisme" });
  const vus = new Set<string>();
  return liens.filter((l) => (vus.has(l.href) ? false : (vus.add(l.href), true)));
}
